import React from 'react';
import { useApp } from '../../context/AppContext';
import {
  Users,
  Layers,
  HelpCircle,
  FileSpreadsheet,
  PlusCircle,
  Upload,
  BookOpen,
  ArrowRight,
  TrendingUp,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';

export const AdminDashboard: React.FC = () => {
  const {
    sectors,
    subjects,
    questionSets,
    questions,
    attempts,
    users,
    setCurrentView,
  } = useApp();

  const totalStudents = users.filter((u) => u.role === 'student').length;

  return (
    <div className="space-y-6 sm:space-y-8 pb-24 md:pb-16">
      {/* 1. Light Modern Admin Hero */}
      <div className="bg-[#F7FAFA] rounded-3xl p-6 sm:p-8 border border-[#E5E9E9] shadow-[0_4px_20px_rgba(17,17,17,0.02)] relative overflow-hidden">
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E8FAF9] text-[#1b8e88] text-xs font-bold uppercase tracking-wider mb-3 border border-[#56E8E1]/30">
            <ShieldCheck className="w-3.5 h-3.5 text-[#1b8e88]" />
            Curriculum Administration Console
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#111111] tracking-tight mb-2">
            Exam & Question Bank Management
          </h1>
          <p className="text-[#5F6368] text-xs sm:text-sm leading-relaxed mb-6 font-normal">
            Configure examination sectors, subjects, and test packages. Create questions individually or bulk import questions with step-by-step CSV parsing.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <button
              id="admin-quick-add-question"
              onClick={() => setCurrentView('admin-questions')}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] font-bold text-xs shadow-xs transition-all cursor-pointer"
            >
              <PlusCircle className="w-4 h-4 text-[#111111]" />
              Manage Question Bank
            </button>

            <button
              id="admin-quick-add-set"
              onClick={() => setCurrentView('admin-sets')}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white hover:bg-[#F0F4F4] text-[#111111] font-bold text-xs border border-[#E5E9E9] shadow-2xs transition-all cursor-pointer"
            >
              <Layers className="w-4 h-4 text-[#1b8e88]" />
              Question Sets & Modules
            </button>

            <button
              id="admin-quick-attempts"
              onClick={() => setCurrentView('admin-attempts')}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white hover:bg-[#F0F4F4] text-[#111111] font-bold text-xs border border-[#E5E9E9] shadow-2xs transition-all cursor-pointer"
            >
              <FileSpreadsheet className="w-4 h-4 text-[#1b8e88]" />
              Student Exam Submissions
            </button>
          </div>
        </div>

        <div className="absolute right-0 top-0 -mt-16 -mr-16 w-80 h-80 rounded-full bg-[#56E8E1]/10 blur-3xl pointer-events-none" />
      </div>

      {/* 2. High-Level KPI Cards Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="text-[10px] font-bold text-[#5F6368] uppercase tracking-wider mb-1">
            MCQ Questions
          </div>
          <div className="text-2xl font-black text-[#111111]">{questions.length}</div>
          <div className="text-[11px] text-[#5F6368] mt-1">Across all sets</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="text-[10px] font-bold text-[#5F6368] uppercase tracking-wider mb-1">
            Question Sets
          </div>
          <div className="text-2xl font-black text-[#1b8e88]">{questionSets.length}</div>
          <div className="text-[11px] text-[#5F6368] mt-1">Exam modules</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="text-[10px] font-bold text-[#5F6368] uppercase tracking-wider mb-1">
            Subjects
          </div>
          <div className="text-2xl font-black text-[#111111]">{subjects.length}</div>
          <div className="text-[11px] text-[#5F6368] mt-1">Topics classified</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="text-[10px] font-bold text-[#5F6368] uppercase tracking-wider mb-1">
            Sectors
          </div>
          <div className="text-2xl font-black text-[#111111]">{sectors.length}</div>
          <div className="text-[11px] text-[#5F6368] mt-1">Curriculum streams</div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="text-[10px] font-bold text-[#5F6368] uppercase tracking-wider mb-1">
            Attempts Logged
          </div>
          <div className="text-2xl font-black text-[#8a6508]">{attempts.length}</div>
          <div className="text-[11px] text-[#5F6368] mt-1">Total student tests</div>
        </div>

        <div
          onClick={() => setCurrentView('admin-users')}
          className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)] hover:border-[#56E8E1] transition-all cursor-pointer group"
        >
          <div className="text-[10px] font-bold text-[#5F6368] uppercase tracking-wider mb-1">
            Active Candidates
          </div>
          <div className="text-2xl font-black text-[#111111] group-hover:text-[#1fb4ac] transition-colors">{totalStudents}</div>
          <div className="text-[11px] text-[#5F6368] mt-1">Registered users</div>
        </div>
      </div>

      {/* 3. Management Modules Navigation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5">
        <div
          onClick={() => setCurrentView('admin-users')}
          className="bg-white p-5 rounded-3xl border border-[#E5E9E9] hover:border-[#56E8E1] transition-all cursor-pointer group flex flex-col justify-between shadow-[0_2px_12px_rgba(17,17,17,0.02)] hover:shadow-md"
        >
          <div>
            <div className="w-11 h-11 rounded-2xl bg-[#E8FAF9] text-[#1b8e88] flex items-center justify-center mb-4 border border-[#56E8E1]/30 group-hover:scale-105 transition-transform">
              <Users className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-[#111111] mb-1">
              User Directory
            </h3>
            <p className="text-xs text-[#5F6368] leading-relaxed mb-4">
              Manage student and admin accounts, add/edit profiles, toggle status, and login as user for support.
            </p>
          </div>
          <div className="flex items-center gap-1 text-xs font-bold text-[#1b8e88] group-hover:translate-x-1 transition-transform">
            Manage Users <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </div>

        <div
          onClick={() => setCurrentView('admin-questions')}
          className="bg-white p-5 rounded-3xl border border-[#E5E9E9] hover:border-[#56E8E1] transition-all cursor-pointer group flex flex-col justify-between shadow-[0_2px_12px_rgba(17,17,17,0.02)] hover:shadow-md"
        >
          <div>
            <div className="w-11 h-11 rounded-2xl bg-[#E8FAF9] text-[#1b8e88] flex items-center justify-center mb-4 border border-[#56E8E1]/30 group-hover:scale-105 transition-transform">
              <HelpCircle className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-[#111111] mb-1">
              Question Bank
            </h3>
            <p className="text-xs text-[#5F6368] leading-relaxed mb-4">
              Add, edit, or delete questions. Filter by set, difficulty, or keyword, and perform CSV bulk import/export.
            </p>
          </div>
          <div className="flex items-center gap-1 text-xs font-bold text-[#1b8e88] group-hover:translate-x-1 transition-transform">
            Open Question Bank <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </div>

        <div
          onClick={() => setCurrentView('admin-sets')}
          className="bg-white p-5 rounded-3xl border border-[#E5E9E9] hover:border-[#EBC138] transition-all cursor-pointer group flex flex-col justify-between shadow-[0_2px_12px_rgba(17,17,17,0.02)] hover:shadow-md"
        >
          <div>
            <div className="w-11 h-11 rounded-2xl bg-[#FFF7D9] text-[#8a6508] flex items-center justify-center mb-4 border border-[#EBC138]/40 group-hover:scale-105 transition-transform">
              <Layers className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-[#111111] mb-1">
              Question Sets
            </h3>
            <p className="text-xs text-[#5F6368] leading-relaxed mb-4">
              Organize tests into published sets, adjust time limits, randomize questions/options, and set exam instructions.
            </p>
          </div>
          <div className="flex items-center gap-1 text-xs font-bold text-[#8a6508] group-hover:translate-x-1 transition-transform">
            Manage Sets <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </div>

        <div
          onClick={() => setCurrentView('admin-sectors')}
          className="bg-white p-5 rounded-3xl border border-[#E5E9E9] hover:border-[#56E8E1] transition-all cursor-pointer group flex flex-col justify-between shadow-[0_2px_12px_rgba(17,17,17,0.02)] hover:shadow-md"
        >
          <div>
            <div className="w-11 h-11 rounded-2xl bg-[#E8FAF9] text-[#1b8e88] flex items-center justify-center mb-4 border border-[#56E8E1]/30 group-hover:scale-105 transition-transform">
              <Sparkles className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-[#111111] mb-1">
              Sectors & Subjects
            </h3>
            <p className="text-xs text-[#5F6368] leading-relaxed mb-4">
              Define exam streams (Competitive, Entrance, Boards) and configure subject taxonomy with custom vector icons.
            </p>
          </div>
          <div className="flex items-center gap-1 text-xs font-bold text-[#1b8e88] group-hover:translate-x-1 transition-transform">
            Configure Curriculum <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </div>

        <div
          onClick={() => setCurrentView('admin-attempts')}
          className="bg-white p-5 rounded-3xl border border-[#E5E9E9] hover:border-[#20A66A] transition-all cursor-pointer group flex flex-col justify-between shadow-[0_2px_12px_rgba(17,17,17,0.02)] hover:shadow-md"
        >
          <div>
            <div className="w-11 h-11 rounded-2xl bg-[#EAF8F1] text-[#20A66A] flex items-center justify-center mb-4 border border-[#20A66A]/20 group-hover:scale-105 transition-transform">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-[#111111] mb-1">
              Student Results Log
            </h3>
            <p className="text-xs text-[#5F6368] leading-relaxed mb-4">
              Inspect candidate exam scores, completion durations, accuracy metrics, and exact question response sheets.
            </p>
          </div>
          <div className="flex items-center gap-1 text-xs font-bold text-[#20A66A] group-hover:translate-x-1 transition-transform">
            View Submissions <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>
    </div>
  );
};
